import React, { useEffect, useState } from "react";
import axios from "axios";
import WelcomeCard from "../../components/common/WelcomeCard";
import { LiaBlogSolid } from "react-icons/lia";
import { FaEdit, FaTrash, FaChevronLeft, FaChevronRight } from "react-icons/fa"; 
import AddBlogModal from "../../components/modals/AddBlogModal";
import EditBlogModal from "../../components/modals/EditBlogModal";
import Loader from "../../components/common/Loader";

const Blogs = () => {
  const [blogList, setBlogList] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedComments, setSelectedComments] = useState([]);
  const [commentModalBlogId, setCommentModalBlogId] = useState(null);
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); 

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  useEffect(() => {
    allBlogs();
  }, []);

  const allBlogs = () => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/blog-post/get-all-blogs`)
      .then((response) => setBlogList(response.data))
      .catch((error) => console.error(error));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      axios
        .delete(`${process.env.REACT_APP_API_URL}/blog-post/delete-blog/${id}`)
        .then(() => {
          alert("Blog deleted successfully");
          allBlogs();
        })
        .catch(() => alert("Failed to delete blog"));
    }
  };

  const handleLike = async (id) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog-post/${id}/like`,
      );
      setBlogList((prev) =>
        prev.map((blog) =>
          blog._id === id ? { ...blog, likes: res.data.likes } : blog,
        ),
      );
    } catch (error) {
      console.error("Error liking blog:", error);
    }
  };

  const openComments = (blog) => {
    setSelectedComments(blog.comments || []);
    setCommentModalBlogId(blog._id);
    setOpenCommentModal(true);
  };

  const handleAddComment = async () => {
    const text = prompt("Enter your comment:");
    if (!text) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog-post/${commentModalBlogId}/comment`,
        { name: "Admin", text },
      );
      setSelectedComments(res.data);
      setBlogList((prev) =>
        prev.map((b) =>
          b._id === commentModalBlogId ? { ...b, comments: res.data } : b,
        ),
      );
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/blog-post/${commentModalBlogId}/comment/${commentId}`,
      );
      setSelectedComments(res.data.comments);
      setBlogList((prev) =>
        prev.map((b) =>
          b._id === commentModalBlogId
            ? { ...b, comments: res.data.comments }
            : b,
        ),
      );
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  const handleShare = async (blog) => {
    try {
      const shareUrl = `${window.location.origin}/blog/${blog._id}`;
      if (navigator.share) {
        await navigator.share({
          title: blog.title,
          text: "Check out this blog!",
          url: shareUrl,
        });
      } else {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          "_blank",
        );
      }
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog-post/${blog._id}/share`,
      );
      setBlogList((prev) =>
        prev.map((b) =>
          b._id === blog._id ? { ...b, shares: res.data.shares } : b,
        ),
      );
    } catch (error) {
      console.error("Error sharing blog:", error);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);
  const handleOpenEditModal = (blog) => {
    setSelectedBlog(blog);
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setSelectedBlog(null);
    setOpenEditModal(false);
  };

  //  Filter blogs by search
  const filteredBlogs = blogList.filter(
    (blog) =>
      blog.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.author?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  //  Pagination on filteredBlogs
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBlogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  return (
    <>
      <AddBlogModal
        open={openAddModal}
        handleOpen={handleOpenAddModal}
        handleClose={handleCloseAddModal}
        allBlogs={allBlogs}
      />

      {selectedBlog && (
        <EditBlogModal
          open={openEditModal}
          blogDetailsData={selectedBlog}
          handleClose={handleCloseEditModal}
          getBlogById={allBlogs}
        />
      )}

      {openCommentModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white w-[90%] max-w-lg rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4 text-[#1C1C25]">Comments</h2>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {selectedComments.length > 0 ? (
                selectedComments.map((c) => (
                  <div
                    key={c._id}
                    className="flex justify-between items-start border-b border-gray-200 pb-2"
                  >
                    <div>
                      <p className="font-semibold text-[#2F9BD6]">{c.name}</p>
                      <p className="text-gray-700">{c.text}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteComment(c._id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No comments yet.</p>
              )}
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={handleAddComment}
                className="bg-[#2F9BD6] text-white px-4 py-2 rounded-md hover:bg-[#1C1C25]"
              >
                Add Comment
              </button>
              <button
                onClick={() => setOpenCommentModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <WelcomeCard />
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-[#000000] tracking-wider">
            Blogs List
          </h1>

          {/* Inline search + button */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-64 border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button
              onClick={handleOpenAddModal}
              className="flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold tracking-wider py-2 px-4 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
            >
              <LiaBlogSolid size={24} className="mr-2" />
              Add New Blog
            </button>
          </div>
        </div>

        {blogList.length === 0 ? (
          <div className="rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4">
            <Loader /> <span className="ml-2">Please Wait..</span>
          </div>
        ) : (
          <div className="container mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {currentItems.map(
                (
                  blog, // ✅ currentItems instead of blogList
                ) => (
                  <div
                    key={blog._id}
                    className="bg-white shadow-md rounded-lg overflow-hidden transition transform hover:scale-105 relative"
                  >
                    <img
                      src={blog.image[0]}
                      alt={blog.title}
                      className="w-full h-48 object-cover object-center"
                    />
                    <div className="p-4">
                      <h2 className="text-xl font-bold mb-2">{blog.title}</h2>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-gray-700">{blog.author}</p>
                        <p className="text-gray-700">
                          {formatDate(blog.dateOfPublish)}
                        </p>
                      </div>
                      <div className="flex justify-between text-sm mt-2 text-gray-600">
                        <button
                          onClick={() => handleLike(blog._id)}
                          className="hover:text-blue-500"
                        >
                          👍 {blog.likes || 0}
                        </button>
                        <button
                          onClick={() => openComments(blog)}
                          className="hover:text-green-600"
                        >
                          💬 {blog.comments?.length || 0}
                        </button>
                        <button
                          onClick={() => handleShare(blog)}
                          className="hover:text-purple-600"
                        >
                          📤 {blog.shares || 0}
                        </button>
                      </div>
                      <div className="flex justify-end space-x-4 mt-4">
                        <button
                          onClick={() => handleOpenEditModal(blog)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <FaEdit size={20} />
                        </button>
                        <button
                          onClick={() => handleDelete(blog._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTrash size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 space-x-2">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-md ${currentPage === 1 ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                >
                  <FaChevronLeft />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`px-3 py-1 rounded-md ${currentPage === page ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-md ${currentPage === totalPages ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}

            {/* Page Info */}
            {filteredBlogs.length > 0 && (
              <div className="text-center mt-4 text-gray-600">
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, filteredBlogs.length)} of{" "}
                {filteredBlogs.length} blogs
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Blogs;
